import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

interface FormData {
  id: string;
  key: string;
  value: string;
  label: string;
}

interface ApiKeyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData: FormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isEditing: boolean;
  isLoading: boolean;
}

export const ApiKeyFormModal: React.FC<ApiKeyFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  onChange,
  isEditing,
  isLoading,
}) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9999]" />
        </Transition.Child>

        <div className="fixed inset-0 z-[9999] w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative z-[10000] transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-black-900"
                      >
                        {isEditing ? "Edit API Key" : "Add New API Key"}
                      </Dialog.Title>
                      <div className="mt-4">
                        <div className="mt-2">
                          <label
                            htmlFor="label"
                            className="block text-sm font-medium leading-6 text-black-800"
                          >
                            Label <span className="text-red-800">*</span>
                          </label>
                          <div className="mt-2">
                            <input
                              id="label"
                              name="label"
                              type="text"
                              value={formData.label}
                              onChange={onChange}
                              placeholder="e.g., Production API Key"
                              disabled={isEditing}
                              className="block w-full rounded-md border border-cyan-800/50 py-2.5 px-3 text-black-900 shadow-sm focus:outline-0 focus:ring-2 focus:ring-cyan-800/50 placeholder:text-black-500/50 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                            />
                          </div>

                          <label
                            htmlFor="key"
                            className="mt-4 block text-sm font-medium leading-6 text-black-800"
                          >
                            Key <span className="text-red-800">*</span>
                          </label>
                          <div className="mt-2">
                            <input
                              id="key"
                              name="key"
                              type="text"
                              value={formData.key}
                              onChange={onChange}
                              placeholder="e.g., BINANCE_API_KEY"
                              disabled={isEditing}
                              className="block w-full rounded-md border border-cyan-800/50 py-2.5 px-3 text-black-900 shadow-sm focus:outline-0 focus:ring-2 focus:ring-cyan-800/50 placeholder:text-black-500/50 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                            />
                          </div>

                          <label
                            htmlFor="value"
                            className="mt-4 block text-sm font-medium leading-6 text-black-800"
                          >
                            Value <span className="text-red-800">*</span>
                          </label>
                          <div className="mt-2">
                            <input
                              id="value"
                              name="value"
                              type="text"
                              value={formData.value}
                              onChange={onChange}
                              placeholder="Enter API key value"
                              className="block w-full rounded-md border border-cyan-800/50 py-2.5 px-3 text-black-900 shadow-sm focus:outline-0 focus:ring-2 focus:ring-cyan-800/50 placeholder:text-black-500/50 sm:text-sm sm:leading-6 font-mono"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    disabled={isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-cyan-300 px-3 py-2 text-sm font-semibold text-black-700 shadow-sm hover:bg-cyan-800 sm:ml-3 sm:w-auto ring-1 ring-inset ring-cyan-800 disabled:opacity-50 transition"
                    onClick={onSubmit}
                  >
                    {isLoading ? (
                      <div className="w-5 h-5 rounded-full animate-spin mx-auto border-2 border-solid border-black-800 border-t-transparent"></div>
                    ) : isEditing ? (
                      "Update"
                    ) : (
                      "Add"
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

